import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import api from '../../../services/api';

// Faz G (spec 2026-08-08 §3/§4, Task 4) — ücret artış/prim turu FE veri
// katmanı. Desen calibration-sessions/config/index.js'ten BİREBİR (bağımsız
// tekrar — repo konvansiyonu, bkz. o dosyanın ResolveUserNamesAsync yorumu):
// GET v1/comp-cycles ICompCycleService.List üzerinden düz bir dizi döner
// (PaginatedList DEĞİL, bkz. task-2-report.md §2), aynı şekilde GET
// v1/comp-cycles/my-team da düz bir dizi (tur bazında gruplu, task-3-report.md §2).

export const CYCLE_STATUS_VALUES = ['draft', 'active', 'review', 'closed', 'cancelled'];
export const CYCLE_TYPES = ['raise', 'bonus'];

export const STATUS_COLORS = {
  draft: { color: '#64748B', bg: '#F1F5F9' },
  active: { color: '#0291EF', bg: '#EFF6FF' },
  review: { color: '#F59E0B', bg: '#FFF7ED' },
  closed: { color: '#16A34A', bg: '#DCFCE7' },
  cancelled: { color: '#E53935', bg: '#FEF2F2' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// --- Dönem seçici (sihirbaz) ------------------------------------------------

// CompCycle.Create/Update yalnız KAPANMIŞ dönemleri kabul eder (spec §2
// "kapanmış+seviyeli dönem" — CompCycleDataAccess InvalidPeriod dalı, bkz.
// task-2-report.md §7). ratingScaleId'siz dönemde matris editörü hiç
// gösterilemeyeceği için (seviye yok) burada da ELENİR — NewSessionModal'ın
// calibrationSessionEnabled erken-uyarı deseniyle AYNI ilke: BE zaten
// reddeder, FE yalnız erken/daha net bir uyarı verir.
export const useEligibleClosedPeriods = () => {
  const [periods, setPeriods] = useState([]);
  const [periodsFetching, setPeriodsFetching] = useState(true);

  useEffect(() => {
    setPeriodsFetching(true);
    api.get('/v1/evaluation-periods', { params: { 'status[eq]': 'closed', pageSize: 100 } })
      .then((res) => {
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        setPeriods(items.filter((p) => !!p.ratingScaleId));
      })
      .catch(() => setPeriods([]))
      .finally(() => setPeriodsFetching(false));
  }, []);

  return { periods, periodsFetching };
};

// Liste sayfasının Dönem kolonu için isim çözümü — TÜM dönemler (durumdan
// bağımsız).
// `enabled` (PMF-05): ad haritası YALNIZ İK sekmesinin tur tablosundaki Dönem
// kolonu için gerekli. İK olmayan kullanıcıda o tablo hiç render edilmez —
// istek de atılmamalı (useCompCyclesApi ile AYNI gerekçe, bkz. oradaki yorum).
export const useAllPeriodsMap = (enabled = true) => {
  const [periodsMap, setPeriodsMap] = useState({});

  useEffect(() => {
    if (!enabled) { setPeriodsMap({}); return; }
    api.get('/v1/evaluation-periods', { params: { pageSize: 200 } })
      .then((res) => {
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        const map = {};
        items.forEach((p) => { map[p.id] = p.name; });
        setPeriodsMap(map);
      });
  }, [enabled]);

  return periodsMap;
};

// Period.RatingScaleId doluysa seviyeleri getirir — calibration-sessions/config
// useScaleLevels ile BİREBİR (bağımsız tekrar).
export const useScaleLevels = (ratingScaleId) => {
  const [levels, setLevels] = useState([]);
  const [levelsFetching, setLevelsFetching] = useState(false);

  useEffect(() => {
    if (!ratingScaleId) {
      setLevels([]);
      return;
    }
    setLevelsFetching(true);
    api.get(`/v1/rating-scales/${ratingScaleId}`)
      .then((res) => setLevels(res.data?.data?.levels || []))
      .catch(() => setLevels([]))
      .finally(() => setLevelsFetching(false));
  }, [ratingScaleId]);

  return { levels, levelsFetching };
};

// Kademeye göre ayrışan matris (spec 2026-10-27 §7.7) için kiracının aktif kademeleri — üst kademeden alta.
export const useActiveGrades = () => {
  const [grades, setGrades] = useState([]);
  useEffect(() => {
    api.get('/v1/job-grades', { params: { pageSize: 200 } })
      .then((res) => {
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        setGrades(items.slice().sort((a, b) => b.level - a.level));
      })
      .catch(() => setGrades([]));
  }, []);
  return grades;
};

// --- Sihirbaz: matris — pür yardımcılar (DOM'suz test edilebilir) ---------

// Kademe satırının matris anahtarı: seviyenin genel satırı `levelId`, kademe satırı `levelId|gradeId`.
export const gradeRowKey = (levelId, gradeId) => `${levelId}|${gradeId}`;

// Değerlendirilecek satırlar: her seviyenin genel satırı + (ayrım açıksa) her kademe satırı.
function matrixRows(levels, matrix, grades, splitByGrade) {
  const rows = [];
  (levels || []).forEach((level) => {
    rows.push({ levelId: level.id, gradeId: null, row: matrix[level.id] });
    if (splitByGrade) {
      (grades || []).forEach((grade) => {
        rows.push({ levelId: level.id, gradeId: grade.id, row: matrix[gradeRowKey(level.id, grade.id)] });
      });
    }
  });
  return rows;
}

// `matrix` şekli: { [levelId]: { min, target, max } } (form state, string
// girişleri). Bir seviye satırı üç durumdan biri OLABİLİR: (1) üçü de boş —
// seviye matrise HİÇ dahil değil (spec §3 "seviyesi matriste olmayan satır
// serbest"), (2) üçü de dolu — geçerli/geçersiz aralık kontrolü yapılır,
// (3) kısmen dolu — HER ZAMAN geçersiz (BE'nin required-alan sıkılaştırması,
// bkz. CompGuidelineRules.ParseGuidelines yorumu — Task 2 Fix round 1 C1).
function rowFillState(row) {
  const { min, target, max } = row || {};
  const values = [min, target, max];
  const filledCount = values.filter((v) => v !== '' && v !== null && v !== undefined).length;
  if (filledCount === 0) return 'empty';
  if (filledCount === 3) return 'full';
  return 'partial';
}

function isRowInRange(row) {
  const min = Number(row.min);
  const target = Number(row.target);
  const max = Number(row.max);
  if (![min, target, max].every(Number.isFinite)) return false;
  return min >= 0 && min <= target && target <= max && max <= 100;
}

// CompGuidelineRules.ParseGuidelines KRİTİK kararı: guidelines listesi HİÇBİR
// ZAMAN boş OLAMAZ ("boş matris" v1'de anlamsız — tur her zaman en az bir
// seviye hedeflemeli, bkz. o dosyanın yorumu). Bu yüzden en az bir TAM/geçerli
// satır ZORUNLU; herhangi bir KISMEN dolu satır (partial) her zaman geçersiz.
export function isGuidelinesMatrixValid(levels, matrix, grades = [], splitByGrade = false) {
  if (!levels || levels.length === 0) return false;
  let hasFullRow = false;
  for (const { row: maybeRow } of matrixRows(levels, matrix, grades, splitByGrade)) {
    const row = maybeRow || {};
    const state = rowFillState(row);
    if (state === 'partial') return false;
    if (state === 'full') {
      if (!isRowInRange(row)) return false;
      hasFullRow = true;
    }
  }
  return hasFullRow;
}

// CompCycleGuidelineRequest sözleşmesiyle camelCase birebir (levelId/minPct/
// targetPct/maxPct) — yalnız TAM dolu satırlar dahil edilir (boş satırlar o
// seviyeyi matris-dışı bırakır, spec §3). "Kademeye Göre Ayır" açıkken dolu kademe satırları gradeId ile gider; boş
// bırakılan kademe satırı gönderilmez → o kademedeki kişi seviyenin genel satırına düşer (spec 2026-10-27 §7.7).
export function buildGuidelinesPayload(levels, matrix, grades = [], splitByGrade = false) {
  return matrixRows(levels, matrix, grades, splitByGrade)
    .filter(({ row }) => rowFillState(row) === 'full')
    .map(({ levelId, gradeId, row }) => {
      return {
        levelId,
        ...(gradeId ? { gradeId } : {}),
        minPct: Number(row.min),
        targetPct: Number(row.target),
        maxPct: Number(row.max),
      };
    });
}

// CompCycleRequest sözleşmesiyle birebir (periodId/name/cycleType/guidelines).
export function buildCycleCreatePayload(form, levels, grades = []) {
  return {
    periodId: form.periodId,
    name: (form.name || '').trim(),
    cycleType: form.cycleType,
    guidelines: buildGuidelinesPayload(levels, form.matrix, grades, form.splitByGrade),
  };
}

export function isCycleFormValid(form, levels, grades = []) {
  if (!form.periodId) return false;
  if (!form.name || !form.name.trim()) return false;
  if (!CYCLE_TYPES.includes(form.cycleType)) return false;
  return isGuidelinesMatrixValid(levels, form.matrix, grades, form.splitByGrade);
}

export const NEW_CYCLE_INITIAL_FORM = {
  periodId: null,
  name: '',
  cycleType: 'raise',
  matrix: {},
  splitByGrade: false,
};

// --- Kapanış hazırlığı (İK detay) — CompGuidelineRules.ReadyToClose ile
// AYNI karar, FE erken-uyarı için ---------------------------------------

// `recommendations`: CompCycleRecommendationResponse[] (finalPct/proposedPct/
// employeeName alanları). Eksik satır = FinalPct VE ProposedPct İKİSİ de boş
// (BE'nin ReadyToClose kararıyla birebir — kapanışta FinalPct boşsa
// ProposedPct devralınır, bu yüzden yalnız FinalPct boş olması "eksik" SAYILMAZ).
export function computeMissingRecommendations(recommendations) {
  return (recommendations || [])
    .filter((r) => r.finalPct === null && r.proposedPct === null)
    .map((r) => r.employeeName);
}

// KAPANIŞTAN ERTELENEN SATIRLAR (bu parti). Close, aktörün KENDİ ücret satırlarını
// kapanıştan MUAF tutar (kilitlenme kaçışı, bkz. CompCycleDataAccess.Close) — o satırlar
// "pending" kalır. Tur "closed" göründüğü hâlde içinde kesinleşmemiş satır durması TEK
// başına görünmüyordu: tablodaki bir satırın "pending" yazması, diğer hepsinin "finalized"
// yazdığı bir listede fark edilmiyor ve "eksik öneri" afişi YALNIZ status === 'review'
// iken çiziliyordu — yani tam gerekli olduğu anda kayboluyordu.
//
// TANIM, BE ile AYNI: Close `deferredNames`i "Status != finalized" satırlardan üretir; burada
// da ölçüt satırın kendi durumudur (finalPct/proposedPct DEĞİL — devralma yapılmadığı için
// ikisi de boş olabilir ama satır yine de İK'nın kendi satırı olduğu için ertelenmiştir).
export function computeDeferredRecommendations(recommendations) {
  return (recommendations || [])
    .filter((r) => r.status !== 'finalized')
    .map((r) => r.employeeName);
}

// --- Liste sayfası: CRUD + yaşam döngüsü aksiyonları ----------------------

// PMF-05 — `enabled`: İK YÜZEYİ uçları (GET/POST/PUT/DELETE v1/comp-cycles)
// sınıf düzeyinde [ModuleAuthorize("PM","Write")] taşır ve servis katmanında
// AYRICA aktif enterpriseAdmin kapısından geçer (CompCycleDataAccess
// .CheckHrActorAsync). Bu hook koşulsuz GET atıyordu: sayfayı açan HER
// PM.Read'li kullanıcı (ör. yalnız "Ekibim" sekmesini kullanan bir yönetici)
// her yüklemede 403 alıyor ve Axios interceptor'ı hata toast'ı basıyordu —
// sayfanın kendisi çalışırken.
//
// DİKKAT — bu sayfada `requiresEnterpriseAdmin` ÇÖZÜM DEĞİLDİR: aynı sayfa
// yöneticinin self-servis "Ekibim" sekmesini de barındırır ve o uçlar
// (CompCycleMyTeamController) BİLEREK [Authorize]-only'dir. Menüyü/rotayı İK'ya
// kapatmak, web+mobilde başka giriş noktası OLMAYAN o yüzeyi tamamen yok
// ederdi. Doğru kapı burada: istek yalnız kullanabilecek kullanıcı için atılır.
export const useCompCyclesApi = (enabled = true) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!enabled) { setData([]); setFetching(false); return; }
    setFetching(true);
    api.get('/v1/comp-cycles')
      .then((res) => setData(Array.isArray(res.data?.data) ? res.data.data : []))
      .finally(() => setFetching(false));
  }, [refreshTrigger, enabled]);

  const createCycle = useCallback(async (payload) => {
    try {
      const res = await api.post('/v1/comp-cycles', payload);
      refresh();
      return res.data?.data?.id || true;
    } catch {
      return null;
    }
  }, [refresh]);

  // verb: activate|review|cancel — CompCycleController'ın POST /{id}/{verb}
  // uçlarıyla birebir (calibration-sessions runLifecycleAction deseni).
  const runLifecycleAction = useCallback(async (id, verb) => {
    try {
      await api.post(`/v1/comp-cycles/${id}/${verb}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deleteDraftCycle = useCallback(async (id) => {
    try {
      await api.delete(`/v1/comp-cycles/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // EmployeeReportsPage.js'in blob-indirme deseniyle BİREBİR (services/api.js
  // interceptor'ı 2xx blob yanıtlarına dokunmuyor, hata durumunda normal JSON
  // hata gövdesi döner — o zaman blob decode edilemez, ama mutation isMutation
  // reject deseni burada GEÇERLİ DEĞİL çünkü bu bir GET; catch bloğu yeterli).
  const exportCycle = useCallback(async (id, fileNameHint) => {
    try {
      const res = await api.get(`/v1/comp-cycles/${id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileNameHint || 'ucret-turu'}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    data, fetching, refresh, createCycle, runLifecycleAction, deleteDraftCycle, exportCycle,
  };
};

// --- Detay: tur + öneri satırları -----------------------------------------

export const useCycleDetail = (cycleId) => {
  const [detail, setDetail] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!cycleId) return;
    setFetching(true);
    api.get(`/v1/comp-cycles/${cycleId}`)
      .then((res) => setDetail(res.data?.data || null))
      .finally(() => setFetching(false));
  }, [cycleId, refreshTrigger]);

  // CompRecommendationFinalizeRequest: { finalPct?, hrNote? } — İKİ ALAN DA
  // BAĞIMSIZ/PATCH-BENZERİ (task-2-report.md §0 C2), bu yüzden çağıran taraf
  // (CycleDetailView'ın inline finalPct/hrNote hücreleri) yalnız DEĞİŞEN alanı
  // gönderir — diğerine ASLA dokunulmaz.
  const finalizeRecommendation = useCallback(async (recommendationId, patch) => {
    try {
      const res = await api.put(`/v1/comp-cycles/${cycleId}/recommendations/${recommendationId}`, patch);
      setDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          recommendations: prev.recommendations.map((r) => (r.id === recommendationId
            ? { ...r, ...res.data?.data }
            : r)),
        };
      });
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message };
    }
  }, [cycleId]);

  // Kapanış — CloseSessionDialog.js'in missingProposals deseniyle AYNI:
  // performance.compCycle.missingRecommendations hatası kendi diyaloğunda ele
  // alınır (skipErrorToast), diğer her sonuç varsayılan toast'a düşer.
  const closeCycle = useCallback(async () => {
    try {
      const res = await api.post(`/v1/comp-cycles/${cycleId}/close`, {}, {
        skipErrorToast: ['performance.compCycle.missingRecommendations'],
      });
      refresh();
      return { ok: true, data: res.data?.data };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message, data: err.response?.data?.data };
    }
  }, [cycleId, refresh]);

  return {
    detail, fetching, refresh, finalizeRecommendation, closeCycle,
  };
};

// --- Ekibim sekmesi: yönetici self-servis ----------------------------------

// GET /v1/comp-cycles/my-team — cycleId VERİLMEZ (tüm aktif turlar + bağlı
// satırlar, tur bazında gruplu — task-3-report.md §6 "cycleId verilmezse TÜM
// aktif turlar döner, FE sekmeler arası ayrım isterse cycleType ile
// gruplamalı"). Boş grup zaten BE'de filtrelenir (çağıranın hiç satırı
// olmayan tur listeye HİÇ girmez).
export const useMyTeamCycles = () => {
  const [cycles, setCycles] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/comp-cycles/my-team')
      .then((res) => setCycles(Array.isArray(res.data?.data) ? res.data.data : []))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  // CompRecommendationProposeRequest: { proposedPct?, proposedNote? } — aynı
  // bağımsız/PATCH-benzeri desen (task-3-report.md §0/§4), BE'nin
  // noteRequired/invalidPct kararı burada TEKRAR EDİLMEZ (client-side yalnız
  // ipucu, asıl kapı BE'de — brief).
  const proposeRecommendation = useCallback(async (rowId, patch) => {
    try {
      const res = await api.put(`/v1/comp-cycles/my-team/recommendations/${rowId}`, patch);
      setCycles((prev) => prev.map((cycle) => ({
        ...cycle,
        rows: cycle.rows.map((row) => (row.id === rowId ? { ...row, ...res.data?.data } : row)),
      })));
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message };
    }
  }, []);

  return {
    cycles, fetching, refresh, proposeRecommendation,
  };
};

// Aralık-dışı not zorunluluğu — CompGuidelineRules.RequiresNote ile AYNI karar
// (client-side ipucu; asıl kapı BE'de). min/max null ise (seviye matriste
// tanımlı değil) HER ZAMAN false.
export function requiresNoteHint(pct, minPct, maxPct) {
  if (minPct === null || minPct === undefined || maxPct === null || maxPct === undefined) return false;
  const n = Number(pct);
  if (!Number.isFinite(n)) return false;
  return n < Number(minPct) || n > Number(maxPct);
}
